import {
    useState,
    useMemo,
    useCallback,
    useRef,
    useEffect,
    startTransition,
    type Key
} from 'react';

import { useMeasureElement } from '../../hooks/useMeasureElement';
import hasDOMMeasurements from '../../utils/hasDOMMeasurements';

import { OVERFLOW_REST_ID } from './Overflow.utils';

export type OverflowItemData = {
    width: number;
    alwaysVisible?: boolean;
};

type OverflowItem<T = unknown> = {
    id: Key;
    data: T;
};

export type OverflowConfig<RawItem = unknown> = {
    mode?: 'end' | 'start';
    visibleItemsAmount?: number;
    minVisibleItemsAmount?: number;
    itemKey?: Key | ((item: RawItem) => Key);
};

const isValidWidth = (width: number | undefined): width is number => {
    return typeof width === 'number' && (width > 0 || !hasDOMMeasurements());
};

export const useOverflow = <RawItem>(
    rawItems: RawItem[],
    {
        mode = 'start',
        visibleItemsAmount,
        minVisibleItemsAmount = 1,
        itemKey = 'id'
    }: OverflowConfig<RawItem> = {}
) => {
    const [itemsContainerRef, { width: containerWidth }] = useMeasureElement({
        disabled: visibleItemsAmount !== undefined
    });
    const [itemsData, setItemsData] = useState(() => new Map<Key, OverflowItemData>());

    const pendingUpdatesRef = useRef<Map<Key, OverflowItemData | null>>(new Map());
    const flushTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const getKey = useCallback(
        (item: RawItem, index: number) => {
            if (typeof itemKey === 'function') {
                return itemKey(item);
            }
            return (itemKey && item?.[itemKey as never]) ?? String(index);
        },
        [itemKey]
    );

    const items = useMemo(
        () =>
            rawItems.map((item, index) => ({
                data: item,
                id: getKey(item, index)
            })),
        [rawItems, getKey]
    );

    const flushBatchedUpdates = useCallback(() => {
        if (pendingUpdatesRef.current.size === 0) {
            return;
        }

        const updates = new Map(pendingUpdatesRef.current);
        pendingUpdatesRef.current.clear();

        startTransition(() => {
            setItemsData((prev) => {
                const newMap = new Map(prev);
                updates.forEach((data, key) => {
                    if (data) {
                        newMap.set(key, data);
                    } else {
                        newMap.delete(key);
                    }
                });
                return newMap;
            });
        });
    }, []);

    // Auto-flush on next frame to ensure updates are applied quickly
    useEffect(() => {
        if (pendingUpdatesRef.current.size > 0) {
            if (flushTimeoutRef.current) {
                clearTimeout(flushTimeoutRef.current);
            }
            flushTimeoutRef.current = setTimeout(flushBatchedUpdates, 0);
        }
        return () => {
            if (flushTimeoutRef.current) {
                clearTimeout(flushTimeoutRef.current);
            }
        };
    });

    const setItemData = useCallback(
        (key: Key, data: OverflowItemData | null) => {
            pendingUpdatesRef.current.set(key, data);
            if (process.env.NODE_ENV === 'test') {
                // flush immediately in tests to simplify assertions
                flushBatchedUpdates();
                return;
            }
            if (flushTimeoutRef.current) {
                clearTimeout(flushTimeoutRef.current);
            }
            flushTimeoutRef.current = setTimeout(flushBatchedUpdates, 0);
        },
        [flushBatchedUpdates]
    );

    const getItemData = useCallback((key: Key) => itemsData.get(key), [itemsData]);

    const calculateTotalWidth = useCallback(
        (itemsToMeasure: OverflowItem[]) => {
            return itemsToMeasure.reduce((total, item) => {
                const data = getItemData(item.id);
                return total + (data?.width ?? 0);
            }, 0);
        },
        [getItemData]
    );

    const collectVisibleItems = useCallback(
        (itemsToProcess: OverflowItem[], availableWidth: number): Key[] => {
            const visibleKeys: Key[] = [];
            let currentWidth = 0;

            const willItemFit = (data: OverflowItemData | undefined): data is OverflowItemData => {
                return isValidWidth(data?.width) && currentWidth + data.width <= availableWidth;
            };

            if (mode === 'start') {
                for (const item of itemsToProcess) {
                    const data = getItemData(item.id);
                    if (willItemFit(data)) {
                        visibleKeys.push(item.id);
                        currentWidth += data.width;
                    } else {
                        break;
                    }
                }
            } else {
                for (let i = itemsToProcess.length - 1; i >= 0; i -= 1) {
                    const data = getItemData(itemsToProcess[i].id);
                    if (willItemFit(data)) {
                        visibleKeys.unshift(itemsToProcess[i].id);
                        currentWidth += data.width;
                    } else {
                        break;
                    }
                }
            }

            return visibleKeys;
        },
        [mode, getItemData]
    );

    const applyMinVisibleItems = useCallback(
        (itemsToLimit: OverflowItem[]): Key[] => {
            const keysToShow =
                mode === 'start'
                    ? itemsToLimit.slice(0, minVisibleItemsAmount)
                    : itemsToLimit.slice(-minVisibleItemsAmount);
            return keysToShow.map((item) => item.id);
        },
        [mode, minVisibleItemsAmount]
    );

    const visibleItemKeys = useMemo(() => {
        if (visibleItemsAmount !== undefined) {
            if (items.length <= visibleItemsAmount) {
                return items.map((item) => item.id);
            }
            const itemsToShow =
                mode === 'start'
                    ? items.slice(0, visibleItemsAmount)
                    : items.slice(-visibleItemsAmount);
            return itemsToShow.map((item) => item.id);
        }

        if (!hasDOMMeasurements()) {
            // the logic below requires actual measurements, so we return all items as visible
            return items.map((item) => item.id);
        }

        if (!containerWidth || itemsData.size === 0) {
            return [];
        }

        const availableWidth = containerWidth;
        const totalItemsWidth = calculateTotalWidth(items);
        const overflowIndicatorData = getItemData(OVERFLOW_REST_ID);
        const overflowIndicatorWidth = overflowIndicatorData?.width ?? 0;
        const alwaysVisibleItemsData = Array.from(itemsData.entries())
            .filter(([, data]) => data.alwaysVisible)
            .map(([key, data]) => ({ id: key, data }));
        const alwaysVisibleItemsWidth = calculateTotalWidth(alwaysVisibleItemsData);
        const widthForItems = availableWidth - overflowIndicatorWidth - alwaysVisibleItemsWidth;

        if (totalItemsWidth <= widthForItems) {
            return items.map((item) => item.id);
        }

        const visibleKeys = collectVisibleItems(items, widthForItems);

        if (visibleKeys.length < minVisibleItemsAmount) {
            const minItemKeys = applyMinVisibleItems(items);
            const minItemsToShow = items.filter((item) => minItemKeys.includes(item.id));
            const minItemsTotalWidth = calculateTotalWidth(minItemsToShow);
            const minItemsWithIndicatorWidth =
                minItemsTotalWidth + overflowIndicatorWidth + alwaysVisibleItemsWidth;

            if (minItemsWithIndicatorWidth > availableWidth) {
                return visibleKeys;
            }

            return minItemKeys;
        }

        return visibleKeys;
    }, [
        items,
        itemsData,
        containerWidth,
        mode,
        visibleItemsAmount,
        minVisibleItemsAmount,
        calculateTotalWidth,
        collectVisibleItems,
        applyMinVisibleItems,
        getItemData
    ]);

    const measuredItems = useMemo(() => {
        if (!hasDOMMeasurements()) {
            return visibleItemKeys;
        }
        return visibleItemKeys.filter((key) => !!itemsData.get(key)?.width);
    }, [visibleItemKeys, itemsData]);

    const hasOverflow = useMemo(
        () => visibleItemKeys.length < items.length,
        [visibleItemKeys.length, items.length]
    );

    const hiddenItems = useMemo(() => {
        const visibleSet = new Set(visibleItemKeys);
        return items.filter((item) => !visibleSet.has(item.id));
    }, [items, visibleItemKeys]);

    const isItemVisible = useCallback(
        (key: Key) => {
            if (key === OVERFLOW_REST_ID) {
                return hasOverflow;
            }
            const itemData = getItemData(key);
            return (
                itemData?.alwaysVisible ||
                (measuredItems.includes(key) && visibleItemKeys.includes(key))
            );
        },
        [visibleItemKeys, hasOverflow, getItemData, measuredItems]
    );

    const isLastItem = useCallback(
        (key: Key) => {
            const visibleItems = items.filter((item) => visibleItemKeys.includes(item.id));
            return visibleItems.length > 0 && visibleItems[visibleItems.length - 1].id === key;
        },
        [items, visibleItemKeys]
    );

    const isFirstItem = useCallback(
        (key: Key) => {
            const visibleItems = items.filter((item) => visibleItemKeys.includes(item.id));
            return visibleItems.length > 0 && visibleItems[0].id === key;
        },
        [items, visibleItemKeys]
    );

    return useMemo(
        () => ({
            setItemData,
            itemsContainerRef,
            isItemVisible,
            hasOverflow,
            hiddenItems,
            items,
            isLastItem,
            isFirstItem
        }),
        [
            setItemData,
            itemsContainerRef,
            isItemVisible,
            hasOverflow,
            hiddenItems,
            items,
            isLastItem,
            isFirstItem
        ]
    );
};

export type OverflowType = ReturnType<typeof useOverflow>;
